'use client';

import { NavigationMenuDemo } from '@/components/layout/navbar';
import { Footer } from '@/components/layout/footer';
import { COMPANY } from '@/lib/constants';
import { useTranslations } from '@/app/providers';

export default function CookiePolicyPage() {
  const t = useTranslations();

  return (
    <>
      <NavigationMenuDemo />
      <main className="min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-6 lg:px-0 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.cookie.title}
          </h1>
          <p className="text-brand-violet-100 mb-12">{t.cookie.lastUpdate}</p>

          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section1.title}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t.cookie.section1.p1}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section2.title}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t.cookie.section2.p1}{' '}
                <strong className="text-white">{COMPANY.legalName}</strong>,{' '}
                {t.cookie.section2.p2} {COMPANY.legalAddress},{' '}
                {t.cookie.section2.p3} {COMPANY.vatNumber}.
              </p>
              <p className="text-slate-300 leading-relaxed mt-4">
                {t.cookie.section2.p4}{' '}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                >
                  {COMPANY.email}
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section3.title}
              </h2>

              <div className="mt-6">
                <h3 className="text-xl font-medium text-white mb-3">
                  {t.cookie.section3.technical.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {t.cookie.section3.technical.p1}
                </p>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm text-slate-300 border border-slate-700 rounded-lg">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-white">
                          {t.cookie.section3.technical.tableName}
                        </th>
                        <th className="px-4 py-3 text-left text-white">
                          {t.cookie.section3.technical.tablePurpose}
                        </th>
                        <th className="px-4 py-3 text-left text-white">
                          {t.cookie.section3.technical.tableDuration}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-700">
                        <td className="px-4 py-3">
                          {t.cookie.section3.technical.cookieName}
                        </td>
                        <td className="px-4 py-3">
                          {t.cookie.section3.technical.cookiePurpose}
                        </td>
                        <td className="px-4 py-3">
                          {t.cookie.section3.technical.cookieDuration}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-medium text-white mb-3">
                  {t.cookie.section3.analytics.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {t.cookie.section3.analytics.p1}
                </p>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm text-slate-300 border border-slate-700 rounded-lg">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-white">
                          {t.cookie.section3.analytics.tableService}
                        </th>
                        <th className="px-4 py-3 text-left text-white">
                          {t.cookie.section3.analytics.tableProvider}
                        </th>
                        <th className="px-4 py-3 text-left text-white">
                          {t.cookie.section3.analytics.tablePurpose}
                        </th>
                        <th className="px-4 py-3 text-left text-white">
                          {t.cookie.section3.analytics.tablePrivacy}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-700">
                        <td className="px-4 py-3">
                          {t.cookie.section3.analytics.clarityService}
                        </td>
                        <td className="px-4 py-3">
                          {t.cookie.section3.analytics.clarityProvider}
                        </td>
                        <td className="px-4 py-3">
                          {t.cookie.section3.analytics.clarityPurpose}
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href="https://privacy.microsoft.com/it-it/privacystatement"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                          >
                            {t.cookie.section3.analytics.link}
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-medium text-white mb-3">
                  {t.cookie.section3.profiling.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {t.cookie.section3.profiling.p1}
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section4.title}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t.cookie.section4.intro}
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>
                  <strong className="text-white">Microsoft Clarity:</strong>{' '}
                  <a
                    href="https://clarity.microsoft.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                  >
                    {t.cookie.section4.clarityTerms}
                  </a>
                </li>
                <li>
                  <strong className="text-white">CookieYes:</strong>{' '}
                  <a
                    href="https://www.cookieyes.com/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                  >
                    {t.cookie.section4.cookieyesPrivacy}
                  </a>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section5.title}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t.cookie.section5.p1}
              </p>
              <p className="text-slate-300 leading-relaxed mt-4">
                {t.cookie.section5.p2}
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>
                  <a
                    href="https://support.google.com/chrome/answer/95647"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                  >
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a
                    href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                  >
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a
                    href="https://support.apple.com/it-it/guide/safari/sfri11471/mac"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                  >
                    Safari
                  </a>
                </li>
                <li>
                  <a
                    href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                  >
                    Microsoft Edge
                  </a>
                </li>
              </ul>
              <p className="text-slate-300 leading-relaxed mt-4">
                <strong className="text-white">{t.cookie.section5.note}</strong>{' '}
                {t.cookie.section5.noteText}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section6.title}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t.cookie.section6.p1}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section7.title}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t.cookie.section7.p1}{' '}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                >
                  {COMPANY.email}
                </a>
                .
              </p>
              <p className="text-slate-300 leading-relaxed mt-4">
                {t.cookie.section7.p2}{' '}
                <a
                  href="/privacy"
                  className="text-brand-violet-300 hover:text-brand-violet-200 underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {t.cookie.section8.title}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t.cookie.section8.p1}
              </p>
            </section>

            <section className="mt-12 p-6 bg-slate-800/30 rounded-xl border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-4">
                {t.cookie.definitions.title}
              </h2>
              <dl className="space-y-4 text-slate-300">
                <div>
                  <dt className="font-medium text-white">
                    {t.cookie.definitions.session.title}
                  </dt>
                  <dd>{t.cookie.definitions.session.text}</dd>
                </div>
                <div>
                  <dt className="font-medium text-white">
                    {t.cookie.definitions.persistent.title}
                  </dt>
                  <dd>{t.cookie.definitions.persistent.text}</dd>
                </div>
                <div>
                  <dt className="font-medium text-white">
                    {t.cookie.definitions.firstParty.title}
                  </dt>
                  <dd>{t.cookie.definitions.firstParty.text}</dd>
                </div>
                <div>
                  <dt className="font-medium text-white">
                    {t.cookie.definitions.thirdParty.title}
                  </dt>
                  <dd>{t.cookie.definitions.thirdParty.text}</dd>
                </div>
              </dl>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
